import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  FiDownload, FiFilter, FiFileText, FiCalendar, FiDollarSign,
  FiTruck, FiUsers, FiRefreshCw, FiFile
} from 'react-icons/fi';

const API_URL = getApiUrl();

export default function DataExportPage() {
  const [reservations, setReservations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    minRevenue: '',
    maxRevenue: '',
    machineType: 'all',
    minBookings: '',
    status: 'all'
  });
  
  // Export options
  const [exportOptions, setExportOptions] = useState({
    includeReservations: true,
    includeCustomers: true,
    includeFinancials: true,
    format: 'csv'
  });

  const [machineTypes, setMachineTypes] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load reservations
      const resResponse = await fetch(`${API_URL}/api/reservations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resResponse.ok) {
        const data = await resResponse.json();
        setReservations(data);
        
        // Extract unique machine types
        const types = [...new Set(data.map(r => r.product_name || r.equipment_name).filter(Boolean))];
        setMachineTypes(types);
      }
      
      // Load customers
      const custResponse = await fetch(`${API_URL}/api/customers?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (custResponse.ok) {
        const data = await custResponse.json();
        setCustomers(data.customers || data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to reservations
  const getFilteredReservations = () => {
    return reservations.filter(res => {
      // Date filter
      if (filters.dateFrom) {
        const resDate = new Date(res.event_date || res.start_date);
        if (resDate < new Date(filters.dateFrom)) return false;
      }
      if (filters.dateTo) {
        const resDate = new Date(res.event_date || res.start_date);
        if (resDate > new Date(filters.dateTo)) return false;
      }
      
      // Revenue filter
      const price = parseFloat(res.total_price) || 0;
      if (filters.minRevenue && price < parseFloat(filters.minRevenue)) return false;
      if (filters.maxRevenue && price > parseFloat(filters.maxRevenue)) return false;
      
      // Machine type filter
      if (filters.machineType !== 'all') {
        const machineName = res.product_name || res.equipment_name || '';
        if (!machineName.toLowerCase().includes(filters.machineType.toLowerCase())) return false;
      }
      
      // Status filter
      if (filters.status !== 'all' && res.status !== filters.status) return false;
      
      return true;
    });
  };

  // Calculate stats
  const filteredReservations = getFilteredReservations();
  const totalRevenue = filteredReservations.reduce((sum, r) => sum + (parseFloat(r.total_price) || 0), 0);
  const avgRevenue = filteredReservations.length > 0 ? totalRevenue / filteredReservations.length : 0;

  // Export functions
  const exportCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('Aucune donnée à exporter');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(';'),
      ...data.map(row => 
        headers.map(header => {
          let value = row[header];
          if (value === null || value === undefined) value = '';
          if (typeof value === 'object') value = JSON.stringify(value);
          // Escape semicolons and quotes
          value = String(value).replace(/"/g, '""');
          if (value.includes(';') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }
          return value;
        }).join(';')
      )
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`Export ${filename} terminé`);
  };

  const exportPDF = async (type) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      params.set('format', 'pdf');
      params.set('type', type);
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);
      if (filters.minRevenue) params.set('min_revenue', filters.minRevenue);
      if (filters.maxRevenue) params.set('max_revenue', filters.maxRevenue);
      if (filters.machineType !== 'all') params.set('machine_type', filters.machineType);
      if (filters.status !== 'all') params.set('status', filters.status);
      
      const response = await fetch(`${API_URL}/api/export/report?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_${type}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Export PDF terminé');
      } else {
        toast.error('Erreur lors de l\'export PDF');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleExportReservations = () => {
    const data = filteredReservations.map(r => ({
      'Date événement': r.event_date || r.start_date,
      'Client': r.client_name,
      'Email': r.client_email,
      'Téléphone': r.client_phone,
      'Type événement': r.event_type,
      'Machine': r.product_name || r.equipment_name || '',
      'Adresse': r.event_address,
      'Durée (h)': r.duration_hours,
      'Prix total': r.total_price,
      'Acompte': r.deposit_amount || '',
      'Statut': r.status,
      'Date création': r.created_at
    }));
    
    if (exportOptions.format === 'csv') {
      exportCSV(data, 'reservations');
    } else {
      exportPDF('reservations');
    }
  };

  const handleExportCustomers = () => {
    const data = customers.map(c => ({
      'Nom': `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.name,
      'Email': c.email,
      'Téléphone': c.phone,
      'Total dépensé': c.total_spent || 0,
      'Nb commandes': c.orders_count || 0,
      'Points fidélité': c.loyalty_points || 0,
      'Tags': (c.tags || []).join(', '),
      'Date création': c.date_created || c.created_at
    }));
    
    if (exportOptions.format === 'csv') {
      exportCSV(data, 'clients');
    } else {
      exportPDF('customers');
    }
  };

  const handleExportFinancials = () => {
    const data = filteredReservations.map(r => ({
      'Date': r.event_date || r.start_date,
      'Client': r.client_name,
      'Machine': r.product_name || r.equipment_name || '',
      'Prix HT': r.total_price,
      'Acompte versé': r.deposit_amount || 0,
      'Solde restant': (parseFloat(r.total_price) || 0) - (parseFloat(r.deposit_amount) || 0),
      'Statut paiement': r.payment_status || 'pending',
      'Mode paiement': r.payment_method || ''
    }));
    
    if (exportOptions.format === 'csv') {
      exportCSV(data, 'financier');
    } else {
      exportPDF('financials');
    }
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      minRevenue: '',
      maxRevenue: '',
      machineType: 'all',
      minBookings: '',
      status: 'all'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-export-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Export de Données</h1>
          <p className="text-slate-500 mt-1">Exportez vos réservations et clients en CSV ou PDF</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <FiRefreshCw className="mr-2 h-4 w-4" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiCalendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{filteredReservations.length}</p>
                <p className="text-sm text-slate-500">Réservations</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiDollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalRevenue.toFixed(0)}€</p>
                <p className="text-sm text-slate-500">CA Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FiTruck className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgRevenue.toFixed(0)}€</p>
                <p className="text-sm text-slate-500">Panier moyen</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FiUsers className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{customers.length}</p>
                <p className="text-sm text-slate-500">Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiFilter className="text-amber-600" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <Label>Date début</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Date fin</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>CA minimum (€)</Label>
              <Input
                type="number"
                value={filters.minRevenue}
                onChange={(e) => setFilters({ ...filters, minRevenue: e.target.value })}
                placeholder="0"
                className="mt-1"
              />
            </div>
            <div>
              <Label>CA maximum (€)</Label>
              <Input
                type="number"
                value={filters.maxRevenue}
                onChange={(e) => setFilters({ ...filters, maxRevenue: e.target.value })}
                placeholder="10000"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Type de machine</Label>
              <Select value={filters.machineType} onValueChange={(v) => setFilters({ ...filters, machineType: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Toutes les machines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les machines</SelectItem>
                  {machineTypes.map((type, i) => (
                    <SelectItem key={i} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="confirmed">Confirmé</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={resetFilters} className="w-full">
                Réinitialiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiDownload className="text-amber-600" />
            Options d'export
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Format Selection */}
          <div>
            <Label className="text-base font-medium">Format d'export</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  checked={exportOptions.format === 'csv'}
                  onChange={() => setExportOptions({ ...exportOptions, format: 'csv' })}
                  className="w-4 h-4 text-amber-600"
                />
                <span className="flex items-center gap-1">
                  <FiFileText className="h-4 w-4" />
                  CSV (Excel)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  checked={exportOptions.format === 'pdf'}
                  onChange={() => setExportOptions({ ...exportOptions, format: 'pdf' })}
                  className="w-4 h-4 text-amber-600"
                />
                <span className="flex items-center gap-1">
                  <FiFile className="h-4 w-4" />
                  PDF
                </span>
              </label>
            </div>
          </div>

          {/* Export Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-2 border-dashed hover:border-amber-400 transition-colors">
              <CardContent className="p-6 text-center">
                <FiCalendar className="h-12 w-12 mx-auto mb-3 text-amber-600" />
                <h4 className="font-semibold mb-2">Réservations</h4>
                <p className="text-sm text-slate-500 mb-4">
                  {filteredReservations.length} réservation(s) avec les filtres actuels
                </p>
                <Button 
                  onClick={handleExportReservations} 
                  disabled={exporting}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                >
                  <FiDownload className="mr-2 h-4 w-4" />
                  Exporter
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-dashed hover:border-blue-400 transition-colors">
              <CardContent className="p-6 text-center">
                <FiUsers className="h-12 w-12 mx-auto mb-3 text-blue-600" />
                <h4 className="font-semibold mb-2">Clients</h4>
                <p className="text-sm text-slate-500 mb-4">
                  {customers.length} client(s) au total
                </p>
                <Button 
                  onClick={handleExportCustomers}
                  disabled={exporting}
                  variant="outline"
                  className="w-full"
                >
                  <FiDownload className="mr-2 h-4 w-4" />
                  Exporter
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-dashed hover:border-green-400 transition-colors">
              <CardContent className="p-6 text-center">
                <FiDollarSign className="h-12 w-12 mx-auto mb-3 text-green-600" />
                <h4 className="font-semibold mb-2">Rapport Financier</h4>
                <p className="text-sm text-slate-500 mb-4">
                  CA total : {totalRevenue.toFixed(2)}€
                </p>
                <Button 
                  onClick={handleExportFinancials}
                  disabled={exporting}
                  variant="outline"
                  className="w-full"
                >
                  <FiDownload className="mr-2 h-4 w-4" />
                  Exporter
                </Button>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Preview Table */}
      <Card>
        <CardHeader>
          <CardTitle>Aperçu des données ({filteredReservations.length} réservations)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-3 font-medium">Date</th>
                  <th className="text-left p-3 font-medium">Client</th>
                  <th className="text-left p-3 font-medium">Machine</th>
                  <th className="text-left p-3 font-medium">Type</th>
                  <th className="text-right p-3 font-medium">Prix</th>
                  <th className="text-center p-3 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filteredReservations.slice(0, 10).map((res, i) => (
                  <tr key={res.id || i} className="border-b hover:bg-slate-50">
                    <td className="p-3">
                      {new Date(res.event_date || res.start_date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="p-3 font-medium">{res.client_name}</td>
                    <td className="p-3">{res.product_name || res.equipment_name || '-'}</td>
                    <td className="p-3">{res.event_type || '-'}</td>
                    <td className="p-3 text-right font-medium">{res.total_price}€</td>
                    <td className="p-3 text-center">
                      <Badge variant={res.status === 'confirmed' ? 'default' : 'secondary'}>
                        {res.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {filteredReservations.length > 10 && (
                  <tr>
                    <td colSpan={6} className="p-3 text-center text-slate-500">
                      ... et {filteredReservations.length - 10} autres réservations
                    </td>
                  </tr>
                )}
                {filteredReservations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      Aucune réservation ne correspond aux filtres
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
